// 9章 防水工事 — やさしい解説
// 原文は src/spec_index.json（PDFから自動生成）。このファイルは手書きで管理する。
// SVGは currentColor を使い、ライト/ダーク両テーマに追従させる。

import type { Commentary } from "../types";

const SVG_PROTECT_EXPOSED = `
<svg viewBox="0 0 660 260" role="img" aria-label="保護防水と露出防水の違い。保護防水は防水層の上に保護コンクリートをかぶせて歩行や紫外線から守る。露出防水は防水層が最上面に見えており軽量で改修しやすい">
  <g fill="currentColor" opacity="0.25">
    <rect x="40" y="150" width="260" height="40"/>
    <rect x="360" y="150" width="260" height="40"/>
  </g>
  <g fill="currentColor" opacity="0.8">
    <rect x="40" y="138" width="260" height="12"/>
    <rect x="360" y="138" width="260" height="12"/>
  </g>
  <g fill="currentColor" opacity="0.45">
    <rect x="40" y="108" width="260" height="30"/>
  </g>
  <g fill="currentColor" font-size="13.5" font-weight="bold" text-anchor="middle">
    <text x="170" y="50">保護防水（2節など）</text>
    <text x="490" y="50">露出防水（3節など）</text>
  </g>
  <g fill="currentColor" font-size="12" text-anchor="middle" opacity="0.85">
    <text x="170" y="100">保護コンクリート（歩行可）</text>
    <text x="170" y="132" opacity="0.7">← 防水層（守られている）</text>
    <text x="490" y="132" opacity="0.7">← 防水層（最上面＝見えている）</text>
    <text x="170" y="175">躯体スラブ</text>
    <text x="490" y="175">躯体スラブ</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="40" y="225">保護防水：屋上を歩行利用できる・防水層が長持ち／重い</text>
    <text x="40" y="245">露出防水：軽い・改修しやすい／紫外線対策に仕上塗料、飛散防止の措置が必要</text>
  </g>
</svg>`;

const SVG_ASPHALT_LAYERS = `
<svg viewBox="0 0 660 300" role="img" aria-label="屋根保護防水密着工法A-1種の層構成。下からコンクリート下地、アスファルトプライマー、アスファルトルーフィング、ストレッチルーフィング2層、アスファルトルーフィング、アスファルトはけ塗り2回、絶縁用シート、溶接金網入り保護コンクリート">
  <g fill="currentColor" opacity="0.25"><rect x="60" y="240" width="540" height="36"/></g>
  <g fill="currentColor" opacity="0.5"><rect x="60" y="228" width="540" height="12"/></g>
  <g fill="currentColor" opacity="0.85"><rect x="60" y="196" width="540" height="32"/></g>
  <g fill="currentColor" opacity="0.4"><rect x="60" y="184" width="540" height="12"/></g>
  <g fill="currentColor" opacity="0.3"><rect x="60" y="148" width="540" height="36"/></g>
  <g stroke="currentColor" stroke-width="1.2" opacity="0.6">
    <line x1="80" y1="166" x2="580" y2="166"/>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.9">
    <text x="70" y="264">① コンクリート下地（直均し仕上げ・十分乾燥）</text>
    <text x="70" y="238">② アスファルトプライマー塗り 0.2kg/m²</text>
    <text x="70" y="218">③ ルーフィング類をアスファルトで交互に積層（流し張り）</text>
    <text x="70" y="180" opacity="0.8">④ 絶縁用シート（ポリエチレンフィルム等）</text>
    <text x="70" y="160">⑤ 保護コンクリート（溶接金網入り・こて仕上げ80mm以上）</text>
  </g>
  <g fill="currentColor" font-size="12.5" opacity="0.85">
    <text x="60" y="60" font-weight="bold">アスファルト防水＝「溶かしたアスファルト＋ルーフィング」のミルフィーユ構造</text>
    <text x="60" y="84">層の数と材料の組合せが「種別」（A-1、A-2、B-1…）として表で決まっている。</text>
    <text x="60" y="106">種別は特記で指定される → 現場はその表どおりの層数・使用量で施工する。</text>
  </g>
</svg>`;

const SVG_ROOFING_LAP = `
<svg viewBox="0 0 660 240" role="img" aria-label="ルーフィングの重ね方。継目は幅方向長手方向とも100ミリ以上重ね、水下側のルーフィングを下にして水が継目に入らないようにする。上下層の継目は同一箇所にしない">
  <g fill="currentColor" opacity="0.35">
    <path d="M60 120 L600 90 L600 110 L60 140 Z"/>
  </g>
  <g fill="currentColor" opacity="0.8">
    <path d="M300 106 L600 90 L600 104 L300 120 Z"/>
    <path d="M60 120 L340 105 L340 119 L60 134 Z"/>
  </g>
  <g stroke="currentColor" stroke-width="1.5">
    <line x1="300" y1="130" x2="340" y2="128"/>
    <line x1="300" y1="122" x2="300" y2="138"/>
    <line x1="340" y1="120" x2="340" y2="136"/>
  </g>
  <g fill="currentColor" font-size="12.5" font-weight="bold" text-anchor="middle">
    <text x="320" y="155">重ね 100mm 以上</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.85">
    <text x="480" y="60">水上（高い方）</text>
    <text x="70" y="100">水下（低い方）</text>
    <text x="60" y="190">・水下側のルーフィングを「下」に敷く → 水が流れても継目に入り込まない（瓦と同じ理屈）</text>
    <text x="60" y="212">・上下層の継目は同一箇所に重ねない（弱点を集中させない）</text>
  </g>
  <g stroke="currentColor" stroke-width="1.8">
    <line x1="560" y1="40" x2="480" y2="46"/>
  </g>
  <g fill="currentColor">
    <polygon points="480,46 493,39 494,51"/>
  </g>
  <g fill="currentColor" font-size="11.5" opacity="0.7">
    <text x="570" y="44">水の流れ</text>
  </g>
</svg>`;

const SVG_TATEAGARI = `
<svg viewBox="0 0 660 260" role="img" aria-label="立上りと平場の張り掛け。立上り部のルーフィングは各層とも平場に150ミリ以上張り掛ける。立上り高さが400ミリ未満の場合は平場のルーフィングをそのまま張り上げてよい">
  <g fill="currentColor" opacity="0.25">
    <rect x="60" y="170" width="420" height="50"/>
    <rect x="440" y="40" width="40" height="180"/>
  </g>
  <g stroke="currentColor" stroke-width="7" stroke-linecap="round" fill="none">
    <path d="M100 166 H436 Q440 166 440 162 V60"/>
  </g>
  <g stroke="currentColor" stroke-width="5" stroke-linecap="round" opacity="0.6" fill="none">
    <path d="M436 60 V158 Q436 162 432 162 H280"/>
  </g>
  <g stroke="currentColor" stroke-width="1.5">
    <line x1="280" y1="140" x2="432" y2="140"/>
    <line x1="280" y1="132" x2="280" y2="148"/>
    <line x1="432" y1="132" x2="432" y2="148"/>
  </g>
  <g fill="currentColor" font-size="12.5" font-weight="bold" text-anchor="middle">
    <text x="356" y="128">張り掛け 150mm 以上</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.85">
    <text x="500" y="120">立上り（パラペット等）</text>
    <text x="100" y="240">平場</text>
    <text x="60" y="46">立上りの各層を平場のルーフィングに150mm以上重ねて張り掛ける</text>
  </g>
  <g fill="currentColor" font-size="11.5" opacity="0.75">
    <text x="60" y="256">※ 立上り高さ400mm未満なら、平場のルーフィングをそのまま張り上げてよい（9.2.4）</text>
  </g>
</svg>`;

const SVG_EXP_JOINT = `
<svg viewBox="0 0 660 280" role="img" aria-label="保護コンクリートの伸縮目地の割付け。周辺の立上り部から600ミリ程度、中間は縦横3000ミリ程度の間隔で設け、目地は立上り面まで達するようにする">
  <g fill="none" stroke="currentColor" stroke-width="2.5">
    <rect x="80" y="40" width="440" height="200"/>
  </g>
  <g stroke="currentColor" stroke-width="1.5" stroke-dasharray="7 4" opacity="0.85">
    <line x1="130" y1="40" x2="130" y2="240"/>
    <line x1="270" y1="40" x2="270" y2="240"/>
    <line x1="410" y1="40" x2="410" y2="240"/>
    <line x1="470" y1="40" x2="470" y2="240"/>
    <line x1="80" y1="85" x2="520" y2="85"/>
    <line x1="80" y1="195" x2="520" y2="195"/>
  </g>
  <g stroke="currentColor" stroke-width="1.2">
    <line x1="80" y1="255" x2="130" y2="255"/>
    <line x1="130" y1="262" x2="270" y2="262"/>
  </g>
  <g fill="currentColor" font-size="11.5" opacity="0.9">
    <text x="84" y="272">周辺600mm程度</text>
    <text x="165" y="276">中間3,000mm程度</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.85">
    <text x="540" y="70">目地は排水溝を</text>
    <text x="540" y="88">含めて立上りの</text>
    <text x="540" y="106">仕上り面まで</text>
    <text x="540" y="124">達すること</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="80" y="30">保護コンクリートの伸縮目地の割付け（平面図・成形伸縮目地材を使用）</text>
  </g>
</svg>`;

const SVG_TORCH_M = `
<svg viewBox="0 0 660 220" role="img" aria-label="トーチ工法。トーチバーナーで改質アスファルトシートの裏面と下地をあぶり、裏面のアスファルトを溶かしながら押し広げて密着させる。重ねは100ミリ以上で溶けたアスファルトがはみ出す程度に加熱する">
  <g fill="currentColor" opacity="0.3">
    <rect x="40" y="170" width="580" height="30"/>
  </g>
  <g fill="currentColor" opacity="0.85">
    <path d="M40 170 H340 A60 60 0 0 0 400 116 L400 110 L420 110 L420 116 A80 80 0 0 1 340 190 H40 Z" opacity="0.7"/>
  </g>
  <g stroke="currentColor" stroke-width="4" stroke-linecap="round">
    <line x1="470" y1="60" x2="440" y2="110"/>
  </g>
  <g fill="currentColor" opacity="0.7">
    <path d="M436 118 q-10 18 4 30 q16 -8 8 -28 Z"/>
  </g>
  <g fill="currentColor" font-size="12.5" font-weight="bold">
    <text x="490" y="60">トーチバーナー</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.85">
    <text x="490" y="90">シート裏面と下地を</text>
    <text x="490" y="108">均一にあぶり溶融</text>
    <text x="60" y="150">改質アスファルトシートを転がしながら張り付ける</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="40" y="216">重ね幅100mm以上。重ね部は改質アスファルトが「はみ出す程度」まであぶって水密に（9.3.4）</text>
  </g>
</svg>`;

const SVG_SEAL_TEST = `
<svg viewBox="0 0 640 200" role="img" aria-label="シーリングの簡易接着性試験。実際の部材にプライマーを塗りシーリング材を充填して硬化後に180度方向に引っ張り、シーリング材自体が切れる凝集破壊なら合格。界面からきれいに剥がれたら不合格">
  <g fill="currentColor" opacity="0.35">
    <rect x="60" y="120" width="220" height="40"/>
    <rect x="360" y="120" width="220" height="40"/>
  </g>
  <g fill="currentColor" opacity="0.8">
    <rect x="120" y="104" width="100" height="16" rx="3"/>
    <path d="M420 104 h100 v16 h-60 q-30 0 -20 -40 l-20 -2 q-8 30 0 42 Z" opacity="0.8"/>
  </g>
  <g stroke="currentColor" stroke-width="1.8">
    <line x1="170" y1="80" x2="170" y2="56"/>
  </g>
  <g fill="currentColor">
    <polygon points="170,50 164,62 176,62"/>
  </g>
  <g fill="currentColor" font-size="12.5" font-weight="bold" text-anchor="middle">
    <text x="170" y="40">180°方向に引っ張る</text>
  </g>
  <g fill="currentColor" font-size="12" text-anchor="middle" opacity="0.85">
    <text x="170" y="182">材料の中で切れる（凝集破壊）＝合格</text>
    <text x="470" y="182">界面からスルッと剥がれる＝不合格</text>
  </g>
</svg>`;

const ch09: Record<string, Commentary> = {
  "9.1.1": {
    plainSummary:
      "この章はアスファルト防水・改質アスファルトシート防水・合成高分子系ルーフィングシート防水・塗膜防水・ケイ酸質系塗布防水と、シーリング工事に適用する。",
    why: "防水は「工法の名前」がたくさん出てきて混乱しがちです。まず大きく、①シート・積層系（2〜4節）②塗る系（5・6節）③目地を埋める（7節 シーリング）の3グループで捉えましょう。",
  },

  "9.1.2": {
    plainSummary:
      "防水工事のゴールはシンプル。所定の材料・形状で、取合い部を含めて「漏水がないこと」。",
    why: "漏水は建物クレームの王様です。そして雨漏りの多くは平場ではなく「取合い部」（立上り・ドレン・配管回り・目地）から起こります。仕様書が取合いの納まりに多くの条文を割いているのはそのためです。",
  },

  "9.1.3": {
    plainSummary:
      "降雨・降雪が予想されるとき、下地が乾いていないとき、著しい低温・強風・高湿のときは施工しない。防水層の施工は監督職員の検査を受け、施工後は損傷させない。",
    points: [
      "防水は「天気と下地の乾燥」が段取りの生命線。天気予報で工程を組む",
      "下地水分の確認（高周波水分計・ビニールシート養生法など）をしてから着手",
      "施工後の後工程（設備・仕上げ）による損傷事故が多い。養生と周知を徹底",
    ],
  },

  "9.2.1": {
    plainSummary:
      "この節は、溶融アスファルトとルーフィング類を交互に積層する「アスファルト防水」（熱工法）のルール。コンクリート下地に適用する。",
    why: "最も歴史が長く信頼性の高い防水工法です。溶かしたアスファルトを接着剤にしてルーフィング（防水シート）を何層も張り重ねるため、多少の施工ムラがあっても層の重なりでカバーできるのが強みです。",
    glossary: [
      {
        term: "熱工法",
        meaning:
          "アスファルトを釜で溶融（約220〜270℃）して施工する伝統的な工法。臭気・火気の管理が必要。",
      },
      {
        term: "ルーフィング",
        meaning: "アスファルトを染み込ませた防水シートの総称。",
      },
    ],
    diagrams: [
      {
        svg: SVG_PROTECT_EXPOSED,
        caption: "保護防水と露出防水の違い（9章の全体像）",
      },
    ],
  },

  "9.2.2": {
    plainSummary:
      "材料の規定。防水工事用アスファルトは3種。ルーフィング類（アスファルトルーフィング1500、ストレッチルーフィング1000等）、断熱材（押出法ポリスチレンフォーム3種bA）、成形伸縮目地材、保護コンクリート（溶接金網 φ6-100目）など。",
    glossary: [
      {
        term: "ストレッチルーフィング",
        meaning:
          "合成繊維不織布にアスファルトを含浸させたルーフィング。伸びが大きく破断しにくい。数字（1000等）は引張時の製品規格。",
      },
      {
        term: "砂付あなあきルーフィング",
        meaning:
          "穴のあいたルーフィング。下地と防水層を部分接着（絶縁）させるために1層目に使う。",
      },
    ],
  },

  "9.2.3": {
    plainSummary:
      "防水層の種別（A-1〜E-2など）ごとに、工程（何を何層・使用量いくら）が表で決まっている。どの種別を使うかは特記による。保護＋密着ならA種、保護＋絶縁ならB種、断熱入りはAI・BI種など。",
    why: "種別の記号は「工法の組合せ」を表しています。A＝保護・密着、B＝保護・絶縁、AI/BI＝それに断熱材入り、D・E＝露出系。絶縁工法は下地の動き（ひび割れ）を防水層に伝えないよう1層目を部分接着にする工法で、既存下地の動きが心配な場合に使われます。現場では特記の種別を確認し、表の層数・使用量どおりに施工します。",
    points: [
      "まず特記で種別（A-2 など）を確認 → 該当の表で工程を読む",
      "立上り部は絶縁用シートを省略するなど、注記に重要情報がある",
    ],
    glossary: [
      {
        term: "密着工法と絶縁工法",
        meaning:
          "密着＝防水層を下地に全面接着。絶縁＝1層目を部分接着し下地の動きを縁切りする工法。絶縁には脱気装置を併用する。",
      },
    ],
    diagrams: [
      {
        svg: SVG_ASPHALT_LAYERS,
        caption: "アスファルト防水（保護・密着）の層構成イメージ（表9.2.3）",
      },
    ],
  },

  "9.2.4": {
    plainSummary:
      "下地は直均し仕上げ・出隅入隅は45°面取り。アスファルトは小塊で溶融し、同一アスファルトの溶融は3時間以内。ルーフィングの重ねは100mm以上で水下側を下に。上下層の継目はずらす。立上りは平場に150mm以上張り掛け（立上り400mm未満は平場をそのまま張り上げ可）。出隅・入隅・打継ぎ部は先に増張り。",
    why: "アスファルト防水の品質は「温度管理」と「重ね方」で決まります。溶融しすぎたアスファルトは劣化して接着力が落ちるので3時間ルール。重ねは瓦と同じで、水下側を下にすれば水は継目に入りません。弱点になりやすい隅や打継ぎ部には、一般部より先に増張り（補強張り）します。",
    points: [
      "溶融がまは施工箇所の近くに。完成した防水層の上に置かない",
      "打継ぎ部：絶縁用テープ＋幅300mm以上のストレッチルーフィング増張り",
      "ルーフドレンに向かって水勾配を確認しながら張る",
    ],
    glossary: [
      {
        term: "増張り",
        meaning:
          "出隅・入隅・ドレン回りなど弱点部に、一般部より先に張る補強のルーフィング。",
      },
    ],
    diagrams: [
      {
        svg: SVG_ROOFING_LAP,
        caption: "ルーフィングの重ね方の原則（9.2.4）",
      },
      {
        svg: SVG_TATEAGARI,
        caption: "立上りと平場の張り掛け（9.2.4）",
      },
    ],
  },

  "9.2.5": {
    plainSummary:
      "保護層の施工。絶縁用シートは立上りに30mm程度張り上げ。保護コンクリートはこて仕上げ80mm以上（タイル仕上げ下は60mm以上）で溶接金網を敷き込む。伸縮目地は周辺から600mm・中間3,000mm程度で、立上り面まで達するように成形伸縮目地材を設置。",
    why: "保護コンクリートは日射で伸び縮みします。伸縮目地がないと保護コンクリートが動いて立上り防水層を押し破る事故（せり出し）が起こります。「周辺600mm・中間3m・立上りまで通す」は保護防水の定番数値です。",
    diagrams: [
      {
        svg: SVG_EXP_JOINT,
        caption: "保護コンクリートの伸縮目地割付け（9.2.5）",
      },
    ],
  },

  "9.3.1": {
    plainSummary:
      "この節は、改質アスファルトシートをトーチ工法（バーナーであぶって融着）または常温粘着工法で施工する「露出防水」のルール。",
    glossary: [
      {
        term: "改質アスファルト",
        meaning:
          "ゴムや樹脂を混ぜて温度特性・耐久性を高めたアスファルト。シート1〜2枚で防水層を作れる。",
      },
      {
        term: "トーチ工法",
        meaning:
          "シート裏面のアスファルトをトーチバーナーで溶かしながら張る工法。溶融釜が不要。",
      },
      {
        term: "常温粘着工法",
        meaning:
          "裏面の粘着層（はく離紙をはがす）で張る火を使わない工法。臭気・火気制限のある現場向き。",
      },
    ],
  },

  "9.3.2": {
    plainSummary:
      "改質アスファルトシートはJIS A 6013により、種類・厚さは特記（露出複層R種3.0mm以上、露出単層R種4.0mm以上など）。増張り用シートは非露出複層R種2.5mm以上。",
  },

  "9.3.3": {
    plainSummary:
      "種別はAS-T1〜T4（トーチ）、AS-J1（常温粘着）、断熱入りはASI-T1・ASI-J1。密着か絶縁か、1層か2層かの組合せで工程表が決まっている。絶縁工法では脱気装置を設ける。",
    glossary: [
      {
        term: "脱気装置",
        meaning:
          "下地に残った水分が日射で水蒸気になったとき、防水層の膨れを防ぐために蒸気を逃がす装置。絶縁工法とセットで使う。",
      },
    ],
  },

  "9.3.4": {
    plainSummary:
      "トーチ工法はシート裏面と下地を均一にあぶり、溶けたアスファルトがはみ出す程度まで加熱して密着させる。重ね幅100mm以上。立上り際500mmは全面密着。末端部は押え金物（ステンレスビス@450以下）＋シール材。ドレン回り・配管回りは増張りしてから。",
    why: "トーチ工法の失敗は「あぶり不足」です。重ね部からアスファルトがはみ出すのが十分に溶けた合図。逆にあぶりすぎるとシートを傷めます。露出防水は端部（末端・ドレン・配管）が弱点なので、押え金物とシール材で確実に留めます。",
    points: [
      "重ね部は「はみ出す程度」が合言葉",
      "出隅・入隅は幅200mm程度の増張り用シートを先に",
      "ALCパネル下地はプライマー使用量が倍（0.4kg/m²）になる",
    ],
    diagrams: [
      {
        svg: SVG_TORCH_M,
        caption: "トーチ工法の施工イメージ（9.3.4）",
      },
    ],
  },

  "9.4.1": {
    plainSummary:
      "この節は、合成高分子系ルーフィングシート（塩ビ系・ゴム系など）を用いるシート防水のルール。",
    why: "1〜2mm程度のシート1枚で防水するため軽くて速い工法です。素材はメンブレン（膜）としての性能が高い反面、1枚勝負なので接合部の施工品質がすべてです。",
  },

  "9.4.2": {
    plainSummary:
      "ルーフィングシートはJIS A 6008により種類・厚さは特記（加硫ゴム系1.2〜1.5mm、塩化ビニル樹脂系1.5mm等）。固定金具は厚さ0.4mm以上の防錆鋼板・ステンレス。断熱材の規定もある。",
    glossary: [
      {
        term: "加硫ゴム系シート",
        meaning: "ゴム弾性のあるシート。伸びが大きく下地の動きに強い。接着工法が主。",
      },
      {
        term: "塩化ビニル樹脂系シート",
        meaning:
          "熱風で溶着接合できるシート。接合部が一体化する。機械的固定工法との相性がよい。",
      },
    ],
  },

  "9.4.3": {
    plainSummary:
      "工法は接着工法（S-F1・S-F2）と機械的固定工法（S-M1・S-M2）、断熱入り（SI-）、屋内保護密着（S-C1）。ALCパネル下地に機械的固定工法は適用しない。接着工法では脱気装置を設ける。",
    glossary: [
      {
        term: "機械的固定工法",
        meaning:
          "シートを接着せず固定金具（ディスク等）で躯体に留める工法。下地の湿り気に強く改修に向く。風圧設計が重要。",
      },
    ],
  },

  "9.4.4": {
    plainSummary:
      "接着工法は接着剤のオープンタイムを確認し、しわなく張ってローラー転圧。重ね幅はゴム系100mm以上（立上りと平場は150mm）、塩ビ系40mm以上で熱風融着＋端部液状シール。立上り末端は押え金物＋シーリング。出入隅角は増張り用シートまたは成形役物で補強。",
    why: "シート防水の接合はゴム系＝接着、塩ビ系＝熱風融着と、素材で方法がまったく違います。塩ビ系の融着は溶けて一体になるため重ね40mmでよく、ゴム系は接着なので100mm以上。数字の違いは「接合の原理」の違いです。",
    points: [
      "3枚重ね部は隙間ができやすい：ゴム系は不定形シール充填、塩ビ系は熱風融着でつぶす",
      "機械的固定はドレン回り300mm程度の位置に固定金具を追加",
    ],
    glossary: [
      {
        term: "オープンタイム",
        meaning:
          "接着剤を塗ってから張り付けるまでの待ち時間。早すぎても遅すぎても接着不良になる。",
      },
    ],
  },

  "9.5.1": {
    plainSummary:
      "この節は、ウレタンゴム系・ゴムアスファルト系の塗膜防水（塗ってつくる防水）のルール。",
    why: "液状の防水材を塗り重ねて継ぎ目のない防水層を作る工法です。複雑な形状・狭い場所に強く、屋上・ベランダの改修で最も多く使われています。",
  },

  "9.5.2": {
    plainSummary:
      "主材料はJIS A 6021の屋根用（ウレタンゴム系高伸長形またはゴムアスファルト系）。立上り部には立上り用または共用を使う。補強布・通気緩衝シート等は製造所指定品。",
    glossary: [
      {
        term: "通気緩衝シート",
        meaning:
          "下地と塗膜の間に敷く通気層付きシート。下地の水分による膨れを脱気装置へ逃がす。絶縁工法（X-1）で使用。",
      },
    ],
  },

  "9.5.3": {
    plainSummary:
      "ウレタンゴム系はX-1（絶縁：通気緩衝シート＋総使用量3.0kg/m²）とX-2（密着：補強布＋計3.0kg/m²）。使用量は硬化物比重1.0の場合の換算値で、2回以上に分けて塗る（平場1工程2.5kg/m²上限）。ゴムアスファルト系はY-1（地下外壁）・Y-2（屋内）。",
    why: "塗膜防水の品質＝「膜厚」です。使用量（kg/m²）は所定の膜厚を確保するための数値なので、材料の出荷量と施工面積で使用量管理をします。一度に厚塗りすると発泡・硬化不良になるため、分割塗りが規定されています。",
    points: [
      "使用量は「硬化物比重1.0」基準。材料が違えば換算する",
      "立上りはすべて密着（X-2）仕様",
    ],
  },

  "9.5.4": {
    plainSummary:
      "出隅45°面取り・入隅直角。打継ぎ部はU字にはつってシーリング充填＋幅100mm以上の補強布で補強塗り。出入隅・ドレン・配管回りも補強布で補強。塗継ぎの重ねは100mm以上、補強布の重ねは50mm以上。",
    points: [
      "ウレタンは可使時間内に使い切る量で練る",
      "補強布は防水材を塗りながら張り込む（後から置かない）",
    ],
  },

  "9.6.1": {
    plainSummary:
      "この節は、ケイ酸質系塗布防水（コンクリートに浸透して結晶で緻密化させる防水）のルール。地下ピットや水槽など躯体防水に使う。",
    why: "ケイ酸質系は「膜を作る」のではなく「コンクリート自体を水密にする」珍しいタイプです。素地が命なので、下地の目地処理・水湿しが工程の中心になります。",
  },

  "9.6.2": {
    plainSummary:
      "主材料は日本建築学会規格JASS 8 M-301適合品。練混ぜ水は水道水。",
  },

  "9.6.3": {
    plainSummary:
      "種別はC-SUI（ポリマー無添加）とC-SUP（ポリマー入り）。いずれも下地処理→2回塗り（0.6〜0.8kg/m²×2）の工程。",
  },

  "9.6.4": {
    plainSummary:
      "下地は金ごて押え。打継ぎ部は目地棒（幅30×深さ30程度）を除去後ポリマーセメントモルタル充填。コーン跡も充填処理。塗布面は清掃後に「水湿し」してから塗る。1回目が指触乾燥してから2回目。24時間以上空いたら再度水湿し。",
    why: "ケイ酸質系は水と反応して結晶を作る材料なので、下地が乾いていると反応できません。「塗る前に水湿し」が最大のポイントで、通常の防水（乾燥が命）と真逆なので混同しないよう注意。",
  },

  "9.7.1": {
    plainSummary:
      "この節は、不定形弾性シーリング材による部材接合部・目地の充填（シーリング工事）のルール。",
    why: "シーリングは「動く目地」を止水する仕事です。サッシ回り・打継ぎ目地・外壁パネル目地など、建物の弱点を最後に埋める重要工種で、防水と並んで漏水クレームに直結します。",
  },

  "9.7.2": {
    plainSummary:
      "シーリング材はJIS A 5758により、種類は被着体の組合せで決まる（表9.7.1）：ガラス回り＝シリコーン系（SR-1）、金属・コンクリート一般＝変成シリコーン系（MS-2）、コンクリート打継ぎ・タイル＝ポリサルファイド系（PS-2）、仕上げありのALC＝ポリウレタン系（PU-2）など。有効期間切れは使用禁止。",
    why: "「どこに何を打つか」の早見表が表9.7.1です。覚え方の軸は2つ：①ガラスにはシリコーン（接着性・耐久性最強。ただし周囲を汚染するので他所には使わない）②上に塗装するならポリウレタン（シリコーン系は塗料がのらない）。異種シーリングの打継ぎは相性問題があるため協議事項です。",
    glossary: [
      {
        term: "変成シリコーン系（MS-2）",
        meaning:
          "汎用性が高く上に塗装も可能なシーリング材。金属・コンクリートの目地の定番。",
      },
      {
        term: "バックアップ材",
        meaning:
          "目地の深さを調整するために詰める発泡材。シーリング材と接着しないものを使う。",
      },
      {
        term: "ボンドブレーカー",
        meaning:
          "目地底に張る絶縁テープ。三面接着を防ぎ二面接着にするための材料。",
      },
    ],
  },

  "9.7.3": {
    plainSummary:
      "目地寸法は特記がなければ：打継ぎ目地・ひび割れ誘発目地は幅20mm以上×深さ10mm以上、ガラス回りは幅・深さ5mm以上、その他は幅・深さ10mm以上。",
    points: [
      "「20×10」「10×10」「ガラス5×5」の3セットで覚える",
      "目地が狭すぎるとシーリングが動きに追従できず切れる",
    ],
  },

  "9.7.4": {
    plainSummary:
      "被着体が5℃以下または50℃以上になるおそれがあるとき・結露のおそれがあるときは作業中止。目地が深ければバックアップ材、所定深さなら目地底にボンドブレーカーで「二面接着」（動きの小さい打継ぎ目地等は三面接着可）。プライマー塗布→指定時間内に充填→へら押え。打始めは交差部・角部から、打継ぎはそぎ継ぎで交差部を避ける。",
    why: "シーリングの寿命を決めるのが接着面の数です。目地は動くので、目地底まで接着（三面接着）すると動きが集中して切れてしまう。だから底を縁切りした二面接着が原則です。例外的に、ほとんど動かない打継ぎ目地などは三面接着でよい、という理屈ごと覚えましょう。",
    points: [
      "5℃以下・50℃以上・結露 → 作業中止の3条件",
      "シリコーン系のはみ出しは硬化後に除去（生乾きで触ると広がる）",
      "2成分形は1班1日1ロットでサンプリング",
    ],
    glossary: [
      {
        term: "二面接着・三面接着",
        meaning:
          "シーリング材が接着する面の数。動く目地は二面（左右のみ）、動かない目地は三面（底も）でよい。",
      },
      {
        term: "そぎ継ぎ",
        meaning: "シーリングの打継ぎを斜めに重ねる継ぎ方。垂直の突付けより水密性が高い。",
      },
    ],
  },

  "9.7.5": {
    plainSummary:
      "外部に面するシーリングは施工前に接着性試験を行う（同一組合せの試験成績書があれば承諾を受けて省略可）。特記がなければ簡易接着性試験：実部材にシーリングを打ち、硬化後180°に引っ張って「凝集破壊」なら合格。",
    why: "シーリングの破壊には、材料自体が切れる凝集破壊と、界面から剥がれる接着破壊があります。凝集破壊は材料の実力どおり＝接着はOKという意味。界面からきれいに剥がれるのは接着不良（プライマー不適合など）のサインで不合格です。",
    diagrams: [
      {
        svg: SVG_SEAL_TEST,
        caption: "簡易接着性試験と合否の見方（9.7.5）",
      },
    ],
  },
};

export default ch09;
